import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Drawer, IconButton, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import { IoCallOutline } from 'react-icons/io5';
import { HiOutlineDocumentText } from 'react-icons/hi2';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { FiUpload } from 'react-icons/fi';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import {
  useCreateHostSms,
  useCreateHostSmsImportOrder,
  useGetBusinessPhoneNumbers,
  useResendHostSmsOtp,
  useUploadHostSmsLoa,
  useVerifyHostSms
} from '@/apis/business/setting/phoneNumbers/hook';
import { useGetTelephonyEndpoints } from '@/apis/business/setting/telephonyEndpoints/hook';
import { TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { BusinessProfileResponse } from '@/apis/business/types';
import { HostSms, PhoneNumberPurchase } from '@/apis/business/setting/phoneNumbers/types';
import {
  formatNumberOrExtension,
  isCanadaNumber,
  isConnectSmsPhoneNumber,
  isUsNumber
} from '@/utils/phoneNumberCapabilities';
import { countryCodeFromPhoneNumber, normalizeCountryCode } from '@/utils/phoneNumberBilling';
import {
  COUNTRY_OPTIONS,
  LOA_ACCEPT,
  PROVIDER_OPTIONS,
  STATE_OPTIONS,
  apiErrorMessage,
  formatHostSmsNumber
} from './hostSmsConstants';
import { hostSmsDefaultsFromBusiness } from './businessFormPrefill';

type FormValues = {
  selected_number_key: string;
  phone_number_purchase_id: string;
  telephony_endpoint_id: string;
  phone_number: string;
  customer_name: string;
  loa_authorizing_person: string;
  address_line1: string;
  address_line2: string;
  city: string;
  zip: string;
  state: string;
  country: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  phoneNumber?: PhoneNumberPurchase | null;
  telephonyEndpoint?: TelephonyEndpoint | null;
  record?: HostSms | null;
  onSubmit?: (record: HostSms) => void;
};

type SelectableHostNumber = {
  key: string;
  source: 'endpoint' | 'purchase';
  id: number;
  phone_number: string;
  provider?: string | null;
  provider_name?: string | null;
  provider_purchased?: boolean;
  country_code?: string | null;
  capabilities?: string[] | null;
  kind?: string | null;
};

const BORDER_COLOR = '#E5E7EB';
const LOA_TEMPLATE_FILENAME = 'LOA_Template.txt';
const LOA_TEMPLATE_CONTENT = `LETTER OF AUTHORIZATION (LOA) - HOSTED MESSAGING

Customer or Business Name:
Authorized Person Name:
Service Address:
Phone Number(s):
Current Provider:

I authorize the hosting of SMS/MMS messaging for the phone number(s) listed above.

Signature:                         Date:
`;

const DEFAULT_VALUES: FormValues = {
  selected_number_key: '',
  phone_number_purchase_id: '',
  telephony_endpoint_id: '',
  phone_number: '',
  customer_name: '',
  loa_authorizing_person: '',
  address_line1: '',
  address_line2: '',
  city: '',
  zip: '',
  state: '',
  country: 'US'
};

const requiredRule = (label: string) => ({ required: `${label} is required` });

const pillSx = { textTransform: 'none', fontWeight: 500, borderRadius: '999px', px: 2.5 } as const;

const outlinedPillSx = {
  ...pillSx,
  borderColor: BORDER_COLOR,
  color: 'text.primary',
  bgcolor: '#fff',
  '&:hover': { borderColor: BORDER_COLOR, bgcolor: '#F9FAFB' }
} as const;

const toTime = (value?: string | null) => (value ? new Date(value).getTime() : 0);

const normalizeProviderKey = (value?: string | null) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const providerOptionValue = (rowOrProvider?: PhoneNumberPurchase | string | null) => {
  const candidates =
    typeof rowOrProvider === 'string' || rowOrProvider == null
      ? [rowOrProvider]
      : [rowOrProvider.provider, rowOrProvider.provider_name];

  for (const candidate of candidates) {
    const key = normalizeProviderKey(candidate);
    if (!key) continue;

    const match = PROVIDER_OPTIONS.find((option) => {
      if (option.value === 'other') return false;
      const optionKey = normalizeProviderKey(option.value);
      const labelKey = normalizeProviderKey(String(option.label));
      return key === optionKey || key === labelKey || key.includes(optionKey) || optionKey.includes(key);
    });
    if (match) return match.value;
  }

  return '';
};

const hasHostedSmsCapability = (row: { capabilities?: string[] | null }) =>
  (Array.isArray(row.capabilities) ? row.capabilities : []).some((cap) => cap.toLowerCase() === 'hosted_sms');

/** SIP registration saved as extension — Host SMS uses main_line_number, not the extension id. */
const isSipRegistrationExtension = (endpoint: TelephonyEndpoint) => {
  const provider = endpoint.provider?.toLowerCase() || '';
  if (!['sip_connectivity', 'ring_central'].includes(provider)) return false;
  if (endpoint.kind === 'extension') return true;
  return String(endpoint.connection_config?.number_type || '').toLowerCase() === 'extension';
};

const hostSmsPhoneNumberForEndpoint = (endpoint: TelephonyEndpoint): string => {
  if (isSipRegistrationExtension(endpoint)) {
    const mainLine = String(endpoint.connection_config?.main_line_number || '').trim();
    if (mainLine) return mainLine;
  }
  return endpoint.phone_number || '';
};

const hostSmsCountryForEndpoint = (endpoint: TelephonyEndpoint): string => {
  const stored = normalizeCountryCode(endpoint.country_code);
  if (stored) return stored;
  return normalizeCountryCode(countryCodeFromPhoneNumber(hostSmsPhoneNumberForEndpoint(endpoint))) || '';
};

/** Numbers from the Numbers tab that can start Host SMS. */
const isHostSmsSelectablePurchase = (row: PhoneNumberPurchase) => {
  if (!isConnectSmsPhoneNumber(row)) return false;
  if (!isUsNumber(row) && !isCanadaNumber(row)) return false;

  const provider = row.provider?.toLowerCase();
  if ((provider === 'twilio' || provider === 'bandwidth') && isCanadaNumber(row)) return false;
  if (row.provider_purchased && provider === 'bandwidth' && isUsNumber(row)) return false;
  if (hasHostedSmsCapability(row)) return false;

  return true;
};

const isHostSmsSelectableEndpoint = (row: TelephonyEndpoint) => {
  if (row.kind === 'extension' && row.provider === 'netsapiens') return false;
  if (hasHostedSmsCapability(row)) return false;

  // SIP extension: host the main line DID, not the extension token.
  if (isSipRegistrationExtension(row)) {
    const mainLine = hostSmsPhoneNumberForEndpoint(row);
    if (!/^\+[1-9]\d{6,14}$/.test(mainLine.trim())) return false;
    const country = hostSmsCountryForEndpoint(row);
    return country === 'US' || country === 'CA';
  }

  if (!isConnectSmsPhoneNumber({ phone_number: row.phone_number, kind: row.kind })) return false;
  if (!isUsNumber(row) && !isCanadaNumber(row)) return false;

  const provider = row.provider?.toLowerCase();
  if ((provider === 'twilio' || provider === 'bandwidth') && isCanadaNumber(row)) return false;
  if (row.kind === 'purchased_did' && provider === 'bandwidth' && isUsNumber(row)) return false;

  return true;
};

const formValuesFor = (
  record: HostSms | null,
  phoneNumber: PhoneNumberPurchase | null,
  telephonyEndpoint: TelephonyEndpoint | null,
  business?: BusinessProfileResponse | null
): FormValues => {
  const details = record?.order_details || {};
  const fromBusiness = hostSmsDefaultsFromBusiness(business);
  const addressLine1 =
    details.address_line1 ||
    [details.house_number, details.street_name].filter(Boolean).join(' ') ||
    fromBusiness.address_line1;
  const purchaseId = record?.phone_number_purchase_id || phoneNumber?.id;
  const endpointId = record?.telephony_endpoint_id || telephonyEndpoint?.id;
  const selectedKey = endpointId ? `endpoint:${endpointId}` : purchaseId ? `purchase:${purchaseId}` : '';

  return {
    ...DEFAULT_VALUES,
    selected_number_key: selectedKey,
    phone_number_purchase_id: purchaseId ? String(purchaseId) : '',
    telephony_endpoint_id: endpointId ? String(endpointId) : '',
    phone_number:
      record?.number ||
      (telephonyEndpoint ? hostSmsPhoneNumberForEndpoint(telephonyEndpoint) : '') ||
      phoneNumber?.phone_number ||
      '',
    customer_name: details.customer_name || fromBusiness.customer_name,
    loa_authorizing_person: details.loa_authorizing_person || '',
    address_line1: addressLine1,
    address_line2: details.address_line2 || '',
    city: details.city || fromBusiness.city,
    zip: details.zip || fromBusiness.zip,
    state: details.state_code || fromBusiness.state,
    country: details.country || fromBusiness.country || DEFAULT_VALUES.country
  };
};

export default function HostSmsDrawer({
  open,
  onClose,
  phoneNumber = null,
  telephonyEndpoint = null,
  record = null,
  onSubmit
}: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const primary = theme.palette.primary.main;
  const primaryLight = theme.palette.primary.light || '#DCEEFF';

  const [step, setStep] = useState<1 | 2>(1);
  const [hostSms, setHostSms] = useState<HostSms | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [loaFile, setLoaFile] = useState<File | null>(null);
  const [loaError, setLoaError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const loaInputRef = useRef<HTMLInputElement>(null);

  const createHostSms = useCreateHostSms();
  const resendOtp = useResendHostSmsOtp();
  const verifyOtp = useVerifyHostSms();
  const submitOrder = useCreateHostSmsImportOrder();
  const uploadLoa = useUploadHostSmsLoa();
  const { data: businessProfile } = useGetBusinessProfile({ include: 'address,locations' });
  const { data: phoneNumbersData, isLoading: phoneNumbersLoading } = useGetBusinessPhoneNumbers(
    { per_page: 100 },
    { enabled: open }
  );
  const { data: endpointsData, isLoading: endpointsLoading } = useGetTelephonyEndpoints(
    { page: 1, per_page: 100 },
    { enabled: open }
  );

  const { control, reset, watch, getValues, setValue, handleSubmit } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES
  });

  const selectedPurchaseId = watch('phone_number_purchase_id');
  const selectedEndpointId = watch('telephony_endpoint_id');
  const phoneValue = watch('phone_number');
  const countryValue = watch('country') || 'US';
  const stateOptions = useMemo(() => STATE_OPTIONS[countryValue] || [], [countryValue]);

  const selectableNumbers = useMemo(() => {
    const endpointRows = (endpointsData?.data ?? []).filter(isHostSmsSelectableEndpoint).map(
      (row): SelectableHostNumber => ({
        key: `endpoint:${row.id}`,
        source: 'endpoint',
        id: row.id,
        phone_number: hostSmsPhoneNumberForEndpoint(row),
        provider: row.provider,
        provider_name: row.provider,
        provider_purchased: row.kind === 'purchased_did',
        country_code: hostSmsCountryForEndpoint(row) || row.country_code,
        capabilities: row.capabilities,
        // Display/host as DID even when inventory kind is SIP extension.
        kind: isSipRegistrationExtension(row) ? 'sip_registration' : row.kind
      })
    );

    const purchaseRows = (phoneNumbersData?.data ?? [])
      .filter(isHostSmsSelectablePurchase)
      .filter((purchase) => !endpointRows.some((endpoint) => endpoint.phone_number === purchase.phone_number))
      .map(
        (row): SelectableHostNumber => ({
          key: `purchase:${row.id}`,
          source: 'purchase',
          id: row.id,
          phone_number: row.phone_number,
          provider: row.provider,
          provider_name: row.provider_name || row.provider,
          provider_purchased: row.provider_purchased,
          country_code: row.country_code,
          capabilities: row.capabilities,
          kind: row.number_type === 'extension' ? 'extension' : 'purchased_did'
        })
      );

    const eligible = [...endpointRows, ...purchaseRows];

    const lockedEndpointId =
      telephonyEndpoint?.id || record?.telephony_endpoint_id || Number(selectedEndpointId) || null;
    if (lockedEndpointId && !eligible.some((row) => row.source === 'endpoint' && row.id === lockedEndpointId)) {
      const locked =
        telephonyEndpoint?.id === lockedEndpointId
          ? telephonyEndpoint
          : (endpointsData?.data ?? []).find((row) => row.id === lockedEndpointId) || null;
      if (locked) {
        eligible.unshift({
          key: `endpoint:${locked.id}`,
          source: 'endpoint',
          id: locked.id,
          phone_number: hostSmsPhoneNumberForEndpoint(locked),
          provider: locked.provider,
          provider_name: locked.provider,
          provider_purchased: locked.kind === 'purchased_did',
          country_code: hostSmsCountryForEndpoint(locked) || locked.country_code,
          capabilities: locked.capabilities,
          kind: isSipRegistrationExtension(locked) ? 'sip_registration' : locked.kind
        });
      }
    }

    const lockedPurchaseId = phoneNumber?.id || record?.phone_number_purchase_id || Number(selectedPurchaseId) || null;
    if (lockedPurchaseId && !eligible.some((row) => row.source === 'purchase' && row.id === lockedPurchaseId)) {
      const locked =
        phoneNumber?.id === lockedPurchaseId
          ? phoneNumber
          : (phoneNumbersData?.data ?? []).find((row) => row.id === lockedPurchaseId) || null;
      if (locked) {
        eligible.unshift({
          key: `purchase:${locked.id}`,
          source: 'purchase',
          id: locked.id,
          phone_number: locked.phone_number,
          provider: locked.provider,
          provider_name: locked.provider_name || locked.provider,
          provider_purchased: locked.provider_purchased,
          country_code: locked.country_code,
          capabilities: locked.capabilities,
          kind: locked.number_type === 'extension' ? 'extension' : 'purchased_did'
        });
      }
    }

    return eligible;
  }, [endpointsData, phoneNumbersData, phoneNumber, telephonyEndpoint, record, selectedPurchaseId, selectedEndpointId]);

  const phoneNumberOptions = useMemo(
    () =>
      selectableNumbers.map((row) => {
        const display =
          row.kind === 'sip_registration' || row.kind === 'purchased_did' || row.kind === 'sip_trunk'
            ? formatHostSmsNumber(row.phone_number) || row.phone_number
            : formatNumberOrExtension(row);
        return {
          value: row.key,
          label: row.provider ? `${display} (${row.provider_name || row.provider})` : display
        };
      }),
    [selectableNumbers]
  );

  const selectedNumber = useMemo(() => {
    if (telephonyEndpoint?.id) {
      return selectableNumbers.find((row) => row.source === 'endpoint' && row.id === telephonyEndpoint.id) || null;
    }
    if (phoneNumber?.id) {
      return selectableNumbers.find((row) => row.source === 'purchase' && row.id === phoneNumber.id) || null;
    }
    if (selectedEndpointId) {
      return (
        selectableNumbers.find((row) => row.source === 'endpoint' && row.id === Number(selectedEndpointId)) || null
      );
    }
    if (selectedPurchaseId) {
      return (
        selectableNumbers.find((row) => row.source === 'purchase' && row.id === Number(selectedPurchaseId)) || null
      );
    }
    return null;
  }, [telephonyEndpoint, phoneNumber, selectedEndpointId, selectedPurchaseId, selectableNumbers]);

  const verified = Boolean(hostSms?.otp_verified);
  const otpSent = Boolean(hostSms && !verified && hostSms.otp_sent_at);
  const otpExpired = otpSent && toTime(hostSms?.otp_expires_at) <= now;
  const resendSeconds = Math.max(0, Math.ceil((toTime(hostSms?.otp_resend_available_at) - now) / 1000));
  const purchasedFromUs = Boolean(
    selectedNumber?.source === 'endpoint'
      ? selectedNumber.kind === 'purchased_did' &&
          ['twilio', 'bandwidth'].includes((selectedNumber.provider || '').toLowerCase())
      : selectedNumber?.provider_purchased
  );
  const numberLocked = Boolean(telephonyEndpoint?.id || phoneNumber?.id || hostSms || record);
  // Provider comes from the selected number (no step-1 dropdown).
  const providerValue =
    hostSms?.provider || providerOptionValue(selectedNumber?.provider) || selectedNumber?.provider || 'other';
  const canRequestOtp = Boolean(selectedNumber?.id && phoneValue && providerValue);
  const numbersLoading = phoneNumbersLoading || endpointsLoading;
  const requestingOtp = createHostSms.isPending || resendOtp.isPending;
  const existingLoaName = hostSms?.loa_filename;
  const fieldsLocked = Boolean(hostSms && !hostSms.editable);

  const applyBusinessDefaults = (forceEmptyOnly = true) => {
    const defaults = hostSmsDefaultsFromBusiness(businessProfile);
    const current = getValues();
    const details = hostSms?.order_details || record?.order_details || {};

    const setIfNeeded = (field: keyof FormValues, value: string, existing?: string | null) => {
      if (existing) return;
      if (forceEmptyOnly && String(current[field] || '').trim()) return;
      if (value) setValue(field, value);
    };

    // Country before state so state options match before the code is applied.
    if (!details.country && defaults.country) {
      if (!forceEmptyOnly || !current.country || current.country === DEFAULT_VALUES.country) {
        setValue('country', defaults.country);
      }
    }
    setIfNeeded('customer_name', defaults.customer_name, details.customer_name);
    setIfNeeded('address_line1', defaults.address_line1, details.address_line1 || details.house_number);
    setIfNeeded('city', defaults.city, details.city);
    setIfNeeded('zip', defaults.zip, details.zip);
    setIfNeeded('state', defaults.state, details.state_code);
  };

  useEffect(() => {
    if (!open) return;

    reset(formValuesFor(record, phoneNumber, telephonyEndpoint, businessProfile));
    setHostSms(record);
    setStep(record?.otp_verified ? 2 : 1);
    setOtpCode('');
    setOtpError(null);
    setLoaFile(null);
    setLoaError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record, phoneNumber, telephonyEndpoint, reset]);

  useEffect(() => {
    if (!open || !businessProfile) return;
    applyBusinessDefaults(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessProfile, hostSms?.id]);

  useEffect(() => {
    if (!open || step !== 2 || !businessProfile) return;
    applyBusinessDefaults(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (!open || !otpSent) return undefined;

    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open, otpSent]);

  useEffect(() => {
    const currentState = getValues('state');
    if (currentState && !stateOptions.some((option) => option.value === currentState)) {
      setValue('state', '');
    }
  }, [countryValue, stateOptions, getValues, setValue]);

  const applySelectedNumber = (key: string) => {
    const row = selectableNumbers.find((item) => item.key === key) || null;
    setValue('selected_number_key', key, { shouldDirty: true, shouldValidate: true });
    setValue('telephony_endpoint_id', row?.source === 'endpoint' ? String(row.id) : '');
    setValue('phone_number_purchase_id', row?.source === 'purchase' ? String(row.id) : '');
    setValue('phone_number', row?.phone_number || '', { shouldDirty: true });
  };

  const hostSmsCreatePayload = () => ({
    host_sms: {
      number: phoneValue,
      provider: providerValue,
      ...(selectedNumber?.source === 'endpoint' ? { telephony_endpoint_id: selectedNumber.id } : {}),
      ...(selectedNumber?.source === 'purchase' ? { phone_number_purchase_id: selectedNumber.id } : {})
    }
  });

  const applyRecord = (result: HostSms) => {
    setHostSms(result);
    setNow(Date.now());
    if (!result.editable) onSubmit?.(result);
  };

  const handleRequestOtp = async () => {
    if (!canRequestOtp || requestingOtp) return;
    setOtpError(null);
    setOtpCode('');

    try {
      const result = hostSms
        ? await resendOtp.mutateAsync(hostSms.id)
        : await createHostSms.mutateAsync(hostSmsCreatePayload());
      applyRecord(result);
    } catch (error) {
      setOtpError(apiErrorMessage(error, 'We could not place the verification call. Please try again.'));
    }
  };

  const handleConfirmOtp = async () => {
    if (!hostSms) return;
    if (otpCode.length !== 6) {
      setOtpError('Enter the 6-digit code you heard on the call.');
      return;
    }
    setOtpError(null);

    try {
      applyRecord(await verifyOtp.mutateAsync({ id: hostSms.id, otp: otpCode }));
    } catch (error) {
      setOtpError(apiErrorMessage(error, 'The code could not be verified.'));
    }
  };

  const handleContinue = async () => {
    if (verified) {
      setStep(2);
      return;
    }
    if (!purchasedFromUs || !selectedNumber?.id) return;
    setOtpError(null);

    try {
      const result = await createHostSms.mutateAsync(hostSmsCreatePayload());
      applyRecord(result);
      if (result.editable && result.otp_verified) setStep(2);
    } catch (error) {
      setOtpError(apiErrorMessage(error, 'This number could not be added for SMS hosting.'));
    }
  };

  const handleUseDifferentNumber = () => {
    setHostSms(null);
    setOtpCode('');
    setOtpError(null);
    if (!phoneNumber?.id && !telephonyEndpoint?.id && !record) {
      setValue('selected_number_key', '');
      setValue('phone_number_purchase_id', '');
      setValue('telephony_endpoint_id', '');
      setValue('phone_number', '');
    }
  };

  const handleDownloadLoa = () => {
    const blob = new Blob([LOA_TEMPLATE_CONTENT], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = LOA_TEMPLATE_FILENAME;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleLoaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;
    setLoaFile(file);
    setLoaError(null);
  };

  const handleLoaReupload = async () => {
    if (!hostSms) return;
    if (!loaFile) {
      setLoaError('Upload the signed Letter of Authorization.');
      return;
    }

    try {
      const result = await uploadLoa.mutateAsync({ id: hostSms.id, file: loaFile });
      setHostSms(result);
      setLoaFile(null);
    } catch {
      // Toast already shown; keep the form open.
    }
  };

  const submitForm = handleSubmit(async (values) => {
    if (!hostSms) return;
    if (fieldsLocked) {
      await handleLoaReupload();
      return;
    }
    if (!loaFile && !existingLoaName) {
      setLoaError('Upload the signed Letter of Authorization.');
      return;
    }

    try {
      const result = await submitOrder.mutateAsync({
        id: hostSms.id,
        loa: loaFile,
        payload: {
          customer_name: values.customer_name.trim(),
          loa_authorizing_person: values.loa_authorizing_person.trim(),
          address_line1: values.address_line1.trim(),
          address_line2: values.address_line2.trim() || undefined,
          city: values.city.trim(),
          state_code: values.state,
          zip: values.zip.trim(),
          country: values.country
        }
      });
      setHostSms(result);
      setLoaFile(null);
      if (result.editable) {
        if (onSubmit) onSubmit(result);
        else onClose();
      }
    } catch {
      // The API error is already shown as a toast; keep the form open so it can be corrected.
    }
  });

  const stepTab = (value: 1 | 2, label: string) => {
    const active = step === value;
    const clickable = value === 1 || verified;
    return (
      <Box
        onClick={() => clickable && setStep(value)}
        py={1.25}
        px={1}
        borderRadius='6px'
        bgcolor={active ? primaryLight : 'transparent'}
        sx={{ cursor: clickable ? 'pointer' : 'default' }}
      >
        <Typography
          fontSize={13}
          fontWeight={500}
          textAlign='center'
          color={active ? 'text.primary' : 'text.secondary'}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const sectionTitle = (title: string) => (
    <Typography fontSize={16} fontWeight={500} color='text.primary' mb={2}>
      {title}
    </Typography>
  );

  const renderVerifyOwnership = () => {
    if (verified) {
      return (
        <>
          <Typography fontSize={14} color='text.secondary' mb={2}>
            Your phone number has been successfully verified.
          </Typography>
          <Alert
            icon={<MdOutlineCheckCircle size={18} />}
            severity='success'
            sx={{
              py: 0.25,
              bgcolor: '#DCFCE7',
              color: '#15803D',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#16A34A', alignItems: 'center' },
              '& .MuiAlert-message': { fontSize: 13 }
            }}
          >
            Number ownership verified.
          </Alert>
        </>
      );
    }

    if (purchasedFromUs) {
      return (
        <>
          <Typography fontSize={14} color='text.secondary'>
            This number was purchased through us, so no verification call is needed. Click Continue to add your business
            details.
          </Typography>
          {otpError && (
            <Typography fontSize={12} color='error.main' mt={1}>
              {otpError}
            </Typography>
          )}
        </>
      );
    }

    if (otpSent) {
      return (
        <>
          <Typography fontSize={14} color='text.secondary' mb={2.5}>
            {otpExpired
              ? 'This code has expired. Request a new call to get a fresh code.'
              : 'We are calling your number now. Answer the call and enter the 6-digit code you hear to verify that you own this number.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
            <TextField
              label='6-digit code'
              placeholder='123456'
              value={otpCode}
              onChange={(event) => {
                setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setOtpError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleConfirmOtp();
              }}
              size='small'
              fullWidth
              autoFocus
              disabled={otpExpired}
              error={Boolean(otpError)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
              sx={{
                bgcolor: '#fff',
                '& .MuiOutlinedInput-root': { height: 40 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_COLOR }
              }}
            />
            <Stack direction='row' spacing={1} flexShrink={0}>
              <Button
                variant='contained'
                color='primary'
                onClick={handleConfirmOtp}
                isPending={verifyOtp.isPending}
                disabled={otpExpired}
                sx={{ ...pillSx, minWidth: 90 }}
              >
                Confirm
              </Button>
              <Button
                variant='outlined'
                onClick={handleRequestOtp}
                isPending={requestingOtp}
                disabled={resendSeconds > 0}
                sx={{ ...outlinedPillSx, whiteSpace: 'nowrap' }}
              >
                {resendSeconds > 0 ? `Call Again in ${resendSeconds}s` : 'Request New Code'}
              </Button>
            </Stack>
          </Stack>
          {otpError && (
            <Typography fontSize={12} color='error.main' mt={1}>
              {otpError}
            </Typography>
          )}
          {!otpError && !otpExpired && typeof hostSms?.otp_attempts_remaining === 'number' && (
            <Typography fontSize={12} color='text.secondary' mt={1}>
              {hostSms.otp_attempts_remaining} attempt{hostSms.otp_attempts_remaining === 1 ? '' : 's'} remaining.
            </Typography>
          )}
          {!phoneNumber?.id && !record && (
            <Typography
              component='button'
              type='button'
              onClick={handleUseDifferentNumber}
              fontSize={13}
              color={primary}
              mt={1.5}
              sx={{ background: 'none', border: 0, p: 0, cursor: 'pointer' }}
            >
              Use a different number
            </Typography>
          )}
        </>
      );
    }

    return (
      <>
        <Typography fontSize={14} color='text.secondary' mb={2}>
          We&apos;ll make a call to your number and share a 6-digit OTP code. This verifies that you own the number. You
          must complete this verification to continue.
        </Typography>
        <Button
          variant='contained'
          color='primary'
          startIcon={<IoCallOutline size={16} />}
          onClick={handleRequestOtp}
          isPending={requestingOtp}
          disabled={!canRequestOtp}
          sx={pillSx}
        >
          Request Call OTP
        </Button>
        {otpError && (
          <Typography fontSize={12} color='error.main' mt={1}>
            {otpError}
          </Typography>
        )}
      </>
    );
  };

  const loaDisplayName = loaFile?.name || existingLoaName;

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 900,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Host SMS on Existing Number
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={3}>
        <Box
          display='grid'
          gridTemplateColumns='1fr 1fr'
          gap={0.5}
          p={0.5}
          border={`1px solid ${BORDER_COLOR}`}
          borderRadius='4px'
          mb={3}
        >
          {stepTab(1, '1. Add & Verify Number')}
          {stepTab(2, '2. Business Information & Authorization')}
        </Box>

        {step === 1 && (
          <Stack spacing={3}>
            {numberLocked && phoneValue && !watch('selected_number_key') ? (
              <Input
                name='phone_number'
                control={control}
                label='Phone Number'
                required
                disabled
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText='Hosted messaging is supported only for US & Canada landline / VoIP numbers.'
              />
            ) : (
              <Select
                name='selected_number_key'
                control={control}
                label='Phone Number'
                required
                rules={requiredRule('Phone number')}
                options={phoneNumberOptions}
                sortOrder='none'
                searchable
                searchPlaceholder='Search number'
                isLoading={numbersLoading}
                disabled={numberLocked}
                onChange={(event) => applySelectedNumber(String(event.target.value ?? ''))}
                helperText={
                  phoneNumberOptions.length
                    ? 'Choose a number from your Numbers list. Hosted messaging is supported only for US & Canada landline / VoIP numbers.'
                    : 'No eligible numbers found. Add a US/Canada number in Numbers first.'
                }
                sx={{ bgcolor: '#fff' }}
              />
            )}

            <Box bgcolor='#F9FAFB' borderRadius='8px' p={2.5}>
              <Stack direction='row' spacing={1} alignItems='center' mb={1}>
                <IoCallOutline size={18} color={primary} />
                <Typography fontSize={15} fontWeight={500} color='text.primary'>
                  Verify ownership
                </Typography>
              </Stack>
              {renderVerifyOwnership()}
            </Box>
          </Stack>
        )}

        {step === 2 && (
          <Box>
            {hostSms?.status === 'failed' && hostSms.order_details?.error && (
              <Alert severity='error' sx={{ mb: 3, borderRadius: '6px', '& .MuiAlert-message': { fontSize: 13 } }}>
                The previous order failed: {hostSms.order_details.error}. Review the details below and submit again.
              </Alert>
            )}

            {fieldsLocked && (
              <Alert severity='info' sx={{ mb: 3, borderRadius: '6px', '& .MuiAlert-message': { fontSize: 13 } }}>
                This order has been submitted. Business details cannot be changed. You can replace the LOA if needed.
              </Alert>
            )}

            {sectionTitle('Subscriber Information')}
            <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={3}>
              <Input
                name='customer_name'
                control={control}
                label='Customer or Business Name'
                placeholder='Enter Name Here'
                required
                disabled={fieldsLocked}
                rules={requiredRule('Customer or business name')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Input
                name='loa_authorizing_person'
                control={control}
                label='Authorizing name from Letter of Authorization (LOA)'
                placeholder='Enter Name Here'
                required
                disabled={fieldsLocked}
                rules={requiredRule('Authorizing name')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {sectionTitle('Service Address')}
            <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={3}>
              <Input
                name='address_line1'
                control={control}
                label='Address Line 1'
                placeholder='121 Business Ave'
                required
                disabled={fieldsLocked}
                rules={{
                  ...requiredRule('Address line 1'),
                  pattern: { value: /^\d+[A-Za-z]?(?:[-/]\d+[A-Za-z]?)?\s+\S/, message: 'Start with the house number' }
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Input
                name='address_line2'
                control={control}
                label='Address Line 2'
                placeholder='Suite, Floor, etc.'
                disabled={fieldsLocked}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Input
                name='city'
                control={control}
                label='City'
                placeholder='New York'
                required
                disabled={fieldsLocked}
                rules={requiredRule('City')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Input
                name='zip'
                control={control}
                label='Postal/ZIP Code'
                placeholder='10001'
                required
                disabled={fieldsLocked}
                rules={requiredRule('Postal/ZIP code')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <Select
                name='state'
                control={control}
                label='State/Province/Region'
                required
                disabled={fieldsLocked}
                rules={requiredRule('State/Province/Region')}
                options={stateOptions}
                sortOrder='none'
              />
              <Select
                name='country'
                control={control}
                label='Country'
                required
                disabled={fieldsLocked}
                rules={requiredRule('Country')}
                options={COUNTRY_OPTIONS}
                sortOrder='none'
              />
            </Box>

            {sectionTitle('Sign LOA')}
            <Box border={`1px solid ${loaError ? theme.palette.error.main : BORDER_COLOR}`} borderRadius='8px' p={2.5}>
              <Typography fontSize={16} fontWeight={500} color='text.primary' mb={1}>
                Letter of Authorization (LOA)
              </Typography>
              <Typography fontSize={14} color='text.secondary' mb={2.5} lineHeight={1.5}>
                The signee must have authority over the use of this number. The address on the LOA must be in the US or
                Canada and the signing email inbox must be monitored.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Button
                  variant='outlined'
                  startIcon={<HiOutlineDocumentText size={16} />}
                  onClick={handleDownloadLoa}
                  sx={outlinedPillSx}
                >
                  Download LOA Template
                </Button>
                <Button
                  variant='outlined'
                  startIcon={<FiUpload size={16} />}
                  onClick={() => loaInputRef.current?.click()}
                  sx={outlinedPillSx}
                >
                  {loaDisplayName ? 'Replace Signed LOA' : 'Upload Signed LOA'}
                </Button>
                <input ref={loaInputRef} type='file' hidden accept={LOA_ACCEPT} onChange={handleLoaChange} />
                {loaDisplayName && (
                  <Stack direction='row' spacing={0.75} alignItems='center' color={primary} minWidth={0}>
                    <HiOutlineDocumentText size={18} style={{ flexShrink: 0 }} />
                    <Typography fontSize={14} color={primary} noWrap>
                      {loaDisplayName}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              {loaError && (
                <Typography fontSize={12} color='error.main' mt={1}>
                  {loaError}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box display='flex' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop={`1px solid ${BORDER_COLOR}`}>
        <Button variant='outlined' onClick={onClose} sx={{ ...outlinedPillSx, minWidth: 80 }}>
          Cancel
        </Button>
        {step === 1 ? (
          <Button
            variant='contained'
            color='primary'
            disabled={!verified && !(purchasedFromUs && providerValue)}
            onClick={handleContinue}
            isPending={purchasedFromUs && createHostSms.isPending}
            sx={{ ...pillSx, minWidth: 100 }}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant='contained'
            color='primary'
            onClick={fieldsLocked ? handleLoaReupload : submitForm}
            isPending={submitOrder.isPending || uploadLoa.isPending}
            disabled={fieldsLocked && !loaFile}
            sx={{ ...pillSx, minWidth: 100 }}
          >
            {fieldsLocked ? 'Reupload LOA' : 'Submit'}
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
